/**
 * DocumentationReader - GitBook-style documentation page
 *
 * A unified documentation experience where:
 * - All documents appear in a hierarchical sidebar tree
 * - Clicking a document shows it in a clean reading pane
 * - Easy navigation without leaving the docs context
 * - Always-on collaborative editing
 *
 * Route: /pr/:shortId/do (with optional /:docId in URL path)
 */

import { useState, useMemo, useCallback, useEffect, useLayoutEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Save,
  Plus,
  ClipboardCheck,
  FileCode,
  FlaskConical,
  FileText,
  Trash2,
  MoreHorizontal,
  List,
  MessageCircle,
  Link2,
} from 'lucide-react';
import { PageLayout } from '../components/layout/PageLayout';
import { PivotSidebar, type PivotDimension } from '../components/shared/PivotSidebar';
import {
  type DocumentCategory,
} from '../components/shared/ReaderSidebar.adapters';
import { DocContent } from '../components/documentation/DocContent';
import { DocListCenter } from '../components/documentation/DocListCenter';
import { DocOutlineContent } from '../components/documentation/DocOutline';
import { CommentsSidebar } from '../components/documentation/CommentsSidebar';
import { RelatedSidebar } from '../components/documentation/RelatedSidebar';
import { PresenceAvatars } from '../components/documentation/PresenceAvatars';
import { CommentPopover } from '../components/documentation/CommentPopover';
import { ReaderCenterShell } from '../components/shared/ReaderCenterShell';
import { TemplateSelector, type RequirementsTemplate } from '../components/requirements-editor-v2/TemplateSelector';
import {
  documentsApi,
  type EditorDocument,
  type CreateDocumentRequest,
  type UpdateDocumentRequest,
  type DocumentRelationType,
} from '../api/documents';
import { useProject } from '../contexts/ProjectContext';
import { useToast } from '../contexts/ToastContext';
import { useUser } from '../contexts/UserContext';
import { useDocumentCollaboration } from '../hooks/useCollaboration';
import { useDocumentComments } from '../hooks/useDocumentComments';
import { useDocumentLinks } from '../hooks/useDocumentLinks';
import { useChatSelection } from '../contexts/ChatSelectionContext';
import { useRightPanelRegistry, type PanelTabRegistration } from '../contexts/RightPanelRegistryContext';
import { AddLinkDialog } from '../components/documentation/AddLinkDialog';
import { ConfirmTrashDialog } from '../components/documentation/ConfirmTrashDialog';

// (sidebar collapse state removed — now handled by GlobalChatSidebar)

// ── Document pivot dimensions ────────────────────────────────────────────────

function getDocCategory(doc: EditorDocument): string {
  if (doc.tags?.includes('requirements')) return 'requirements';
  if (doc.doc_type === 'spec') return 'specifications';
  if (doc.doc_type === 'test') return 'tests';
  if (doc.doc_type === 'content') return 'content';
  return 'general';
}

const DOC_CATEGORY_LABELS: Record<string, string> = {
  requirements: 'Requirements',
  specifications: 'Specifications',
  tests: 'Test Plans',
  general: 'General',
  content: 'Content',
};

const DOC_STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

const DOC_DIMENSIONS: PivotDimension<EditorDocument>[] = [
  {
    id: 'hierarchy',
    label: 'Hierarchy',
    // getKey is required by the type but unused when PivotSidebar detects this
    // pivot id and switches to tree rendering mode via getParentId.
    getKey: (_doc) => 'all',
  },
  {
    id: 'doc_type',
    label: 'Doc Type',
    getKey: (doc) => {
      const cat = getDocCategory(doc);
      return cat === 'content' ? null : cat; // hide content-type docs
    },
    getLabel: (key) => DOC_CATEGORY_LABELS[key] || key,
  },
  {
    id: 'status',
    label: 'Status',
    getKey: (doc) => doc.status,
    getLabel: (key) => DOC_STATUS_LABELS[key] || key,
  },
];

// ── Props ────────────────────────────────────────────────────────────────────

interface DocumentationReaderProps {
  /** Route segment used in URL building (default: 'do') */
  routeSegment?: string;
  /** Pane code passed to buildProjectUrl (default: undefined = auto-detect) */
  paneCode?: string;
  /** Sidebar title (default: 'Documents') */
  sidebarTitle?: string;
}

export default function DocumentationReader({
  routeSegment = 'documents',
  paneCode,
  sidebarTitle = 'Documents',
}: DocumentationReaderProps = {}) {
  const params = useParams<{ docId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { project, shortId, buildProjectUrl } = useProject();
  const toast = useToast();
  const { user } = useUser();
  const chatSelection = useChatSelection();

  // State
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [createType, setCreateType] = useState<DocumentCategory>('general');

  const projectId = project?.id;

  // From URL params only — hooks use this (they disable when undefined)
  const activeDocId = params.docId || undefined;

  // Collaboration state
  const {
    ydoc,
    awareness,
    isConnected,
    isConnecting,
    isSynced: isCollaborationSynced,
    connectedUsers,
  } = useDocumentCollaboration({
    docId: activeDocId || '',
    userId: user?.id || 'anonymous',
    userName: user?.name || 'Anonymous',
    enabled: !!activeDocId && !!user,
  });

  // Build collaboration config for MarthaEditor when Yjs is connected
  const collaborationConfig = useMemo(() => {
    if (!ydoc || !isConnected) return undefined;
    return {
      fragment: ydoc.getXmlFragment('document-content'),
      user: { name: user?.name || 'Anonymous', color: '#f97316' },
      ...(awareness ? { provider: { awareness } } : {}),
    };
  }, [ydoc, isConnected, awareness, user?.name]);

  // Comments state
  const {
    comments,
    repliesMap,
    activeCommentId,
    setActiveCommentId,
    createComment,
    replyToComment,
    resolveComment,
    isReplying,
    isResolving,
  } = useDocumentComments({
    documentId: activeDocId || '',
    projectId: projectId || '',
    userId: user?.id || 'anonymous',
    userName: user?.name || 'Anonymous',
    enabled: !!activeDocId && !!projectId && !!user,
  });

  // Links state
  const {
    links: documentLinks,
    count: linksCount,
    createLink,
    deleteLink,
    isCreating: isLinkCreating,
    isDeleting: isLinkDeleting,
  } = useDocumentLinks({
    documentId: activeDocId || '',
    projectId: projectId || '',
    enabled: !!activeDocId && !!projectId,
  });

  const [showAddLinkDialog, setShowAddLinkDialog] = useState(false);
  const [showTrashDialog, setShowTrashDialog] = useState(false);
  const [showDocActions, setShowDocActions] = useState(false);

  // Text selection state for inline comments
  const [commentSelection, setCommentSelection] = useState<{
    text: string;
    start: number;
    end: number;
    position: { x: number; y: number };
  } | null>(null);

  // Handle text selection from editor (no longer shows the floating popover automatically)
  const handleSelectionChange = useCallback((
    selectedText: string | null,
    selectionStart: number,
    selectionEnd: number
  ) => {
    if (selectedText && selectedText.trim().length > 0) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        // Store selection info but don't show popover until comment button clicked
        setCommentSelection({
          text: selectedText,
          start: selectionStart,
          end: selectionEnd,
          position: { x: rect.left + rect.width / 2, y: rect.top - 10 }
        });
      }
    } else {
      setCommentSelection(null);
    }
  }, []);

  // Tracks whether comment popover should be visible (triggered by toolbar button)
  const [showCommentPopover, setShowCommentPopover] = useState(false);

  // Handle comment button click from toolbar
  const handleCommentButtonClick = useCallback(() => {
    // Get current selection from window
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && selection.toString().trim()) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setCommentSelection({
        text: selection.toString(),
        start: 0,
        end: selection.toString().length,
        position: { x: rect.left + rect.width / 2, y: rect.bottom + 10 }
      });
      setShowCommentPopover(true);
    }
  }, []);

  // Handle comment submission from popover
  const handleCreateComment = useCallback(async (content: string) => {
    if (!commentSelection) return;
    await createComment({
      selectionStart: commentSelection.start,
      selectionEnd: commentSelection.end,
      selectedText: commentSelection.text,
      content,
    });
    setCommentSelection(null);
  }, [commentSelection, createComment]);

  // Right panel tab registration
  const { registerTabs, unregisterTabs } = useRightPanelRegistry();


  // Fetch all project documents
  const { data: documentsResponse, isLoading: isLoadingDocs } = useQuery({
    queryKey: ['project-documents', projectId],
    queryFn: () => documentsApi.list(projectId!, { page_size: 200 }),
    enabled: !!projectId,
  });

  // Filter out content-service docs — those belong in the GTM Library, not Product Documentation
  const documents = useMemo(
    () => (documentsResponse?.documents || []).filter((d) => d.doc_type !== 'content'),
    [documentsResponse]
  );

  // Default to "Hierarchy" pivot when any doc has a parent_doc_id (tree exists)
  const defaultDimension = useMemo(
    () => (documents.some((d) => d.parent_doc_id) ? 'hierarchy' : 'doc_type'),
    [documents]
  );

  // Get ordered list for navigation (sorted by type, then title)
  const orderedDocs = useMemo(() => {
    return [...documents].sort((a, b) => {
      const typeOrder = { spec: 1 };
      const aIsReq = a.tags?.includes('requirements') ? 0 : 1;
      const bIsReq = b.tags?.includes('requirements') ? 0 : 1;
      if (aIsReq !== bIsReq) return aIsReq - bIsReq;

      const aType = typeOrder[a.doc_type as keyof typeof typeOrder] ?? 2;
      const bType = typeOrder[b.doc_type as keyof typeof typeOrder] ?? 2;
      if (aType !== bType) return aType - bType;

      return a.title.localeCompare(b.title);
    });
  }, [documents]);

  // Resolved doc ID — falls back to first sorted doc when URL has no docId
  const resolvedDocId = activeDocId || orderedDocs[0]?.id || undefined;

  // Silently update the URL for bookmarkability when we derived the docId
  useLayoutEffect(() => {
    if (!params.docId && resolvedDocId) {
      navigate(buildProjectUrl(`${routeSegment}/${resolvedDocId}`, paneCode), { replace: true });
    }
  }, [params.docId, resolvedDocId, navigate, buildProjectUrl, routeSegment, paneCode]);

  // Fetch active document details — keepPreviousData prevents loading flash during doc-to-doc nav
  const { data: activeDocument, isLoading: isLoadingDoc, error: docError } = useQuery({
    queryKey: ['document', projectId, resolvedDocId],
    queryFn: () => documentsApi.get(resolvedDocId!, projectId!),
    enabled: !!projectId && !!resolvedDocId,
    placeholderData: keepPreviousData,
  });

  // Current index for navigation
  const currentIndex = resolvedDocId
    ? orderedDocs.findIndex((d) => d.id === resolvedDocId)
    : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < orderedDocs.length - 1;

  // Create document mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateDocumentRequest) => documentsApi.create(projectId!, data),
    onSuccess: (newDoc) => {
      queryClient.invalidateQueries({ queryKey: ['project-documents', projectId] });
      toast.success('Document created successfully');
      navigate(buildProjectUrl(`${routeSegment}/${newDoc.id}`, paneCode));
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to create document';
      toast.error(message);
    },
  });

  // Update document mutation (silent on success - auto-save indicator in header handles feedback)
  const updateMutation = useMutation({
    mutationFn: ({ docId, data }: { docId: string; data: UpdateDocumentRequest }) =>
      documentsApi.update(docId, projectId!, data),
    onSuccess: () => {
      // Silently invalidate - no toast for auto-save (header shows "Auto-saved" indicator)
      queryClient.invalidateQueries({ queryKey: ['project-documents', projectId] });
      queryClient.invalidateQueries({ queryKey: ['document', projectId, resolvedDocId] });
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to save document';
      toast.error(message);
    },
  });

  // Handle document selection
  const handleDocumentSelect = useCallback(
    (doc: EditorDocument) => {
      navigate(buildProjectUrl(`${routeSegment}/${doc.id}`, paneCode));
    },
    [navigate, buildProjectUrl, routeSegment, paneCode]
  );

  // Handle create document
  const handleCreateDocument = useCallback(
    (type: DocumentCategory) => {
      setCreateType(type);
      if (type === 'requirements') {
        // Show template selector for requirements
        setShowTemplateSelector(true);
      } else {
        // Direct creation for other types
        const docTypeMap: Record<DocumentCategory, { doc_type: 'spec' | 'document' | 'test'; tags: string[] }> = {
          requirements: { doc_type: 'document', tags: ['requirements'] },
          specifications: { doc_type: 'spec', tags: [] },
          tests: { doc_type: 'test', tags: [] },
          general: { doc_type: 'document', tags: [] },
        };
        const config = docTypeMap[type];
        const labelMap: Record<DocumentCategory, string> = {
          requirements: 'Requirement',
          specifications: 'Specification',
          tests: 'Test Plan',
          general: 'Document',
        };
        const label = labelMap[type];
        createMutation.mutate({
          title: `New ${label}`,
          content: `# New ${label}\n\n*Start writing here...*\n`,
          doc_type: config.doc_type,
          tags: config.tags,
        });
      }
    },
    [createMutation]
  );

  // Handle template selection (for requirements)
  const handleTemplateSelect = useCallback(
    (template: RequirementsTemplate) => {
      setShowTemplateSelector(false);
      const content = generateTemplateContent(template);
      createMutation.mutate({
        title: `New ${template.name}`,
        content,
        doc_type: 'document',
        tags: ['requirements', template.id],
        metadata: {
          doc_category: 'requirements',
          template_id: template.id,
          template_name: template.name,
        },
      });
    },
    [createMutation]
  );

  // Handle save — content format may change (markdown → blocknote) on first edit.
  // Pull the mutate function off the mutation object and depend on that directly:
  // react-query guarantees `mutate` is stable, but the wrapper object is a new
  // reference on every render, so putting `updateMutation` in deps would rebuild
  // handleSave (and every callback downstream) on every render.
  const updateMutate = updateMutation.mutate;
  const handleSave = useCallback(
    (content: string, contentFormat?: string) => {
      if (!resolvedDocId) return;
      const data: UpdateDocumentRequest = { content };
      if (contentFormat === 'blocknote' || contentFormat === 'markdown' || contentFormat === 'html' || contentFormat === 'plain') {
        data.content_format = contentFormat;
      }
      updateMutate({ docId: resolvedDocId, data });
    },
    [resolvedDocId, updateMutate]
  );

  // Handle title change — same stability concern as handleSave.
  const handleTitleChange = useCallback(
    (title: string) => {
      if (!resolvedDocId) return;
      updateMutate({ docId: resolvedDocId, data: { title } });
    },
    [resolvedDocId, updateMutate]
  );

  // Navigation handlers
  const handleNavigatePrev = useCallback(() => {
    if (hasPrev) {
      const prevDoc = orderedDocs[currentIndex - 1];
      navigate(buildProjectUrl(`${routeSegment}/${prevDoc.id}`, paneCode));
    }
  }, [hasPrev, orderedDocs, currentIndex, navigate, buildProjectUrl, routeSegment, paneCode]);

  const handleNavigateNext = useCallback(() => {
    if (hasNext) {
      const nextDoc = orderedDocs[currentIndex + 1];
      navigate(buildProjectUrl(`${routeSegment}/${nextDoc.id}`, paneCode));
    }
  }, [hasNext, orderedDocs, currentIndex, navigate, buildProjectUrl, routeSegment, paneCode]);

  // Handle comment click (scroll to commented text and flash highlight)
  const handleCommentClick = useCallback(
    (comment: { id: string; selectionStart: number; selectedText?: string }) => {
      setActiveCommentId(comment.id);

      const selectedText = (comment as { selectedText?: string }).selectedText;
      if (!selectedText) return;

      // Find the text in the editor DOM using a TreeWalker
      const editorEl = document.querySelector('.blocknote-editor');
      if (!editorEl) return;

      const walker = document.createTreeWalker(editorEl, NodeFilter.SHOW_TEXT);
      let node: Text | null;
      while ((node = walker.nextNode() as Text | null)) {
        const idx = node.textContent?.indexOf(selectedText) ?? -1;
        if (idx === -1) continue;

        // Found the text — wrap it in a highlight span
        const range = document.createRange();
        range.setStart(node, idx);
        range.setEnd(node, idx + selectedText.length);

        const highlight = document.createElement('span');
        highlight.className = 'comment-highlight-flash';
        range.surroundContents(highlight);

        // Scroll into view
        highlight.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Remove the wrapper after animation completes
        setTimeout(() => {
          const parent = highlight.parentNode;
          if (parent) {
            parent.replaceChild(document.createTextNode(selectedText), highlight);
            parent.normalize();
          }
        }, 3000);

        break;
      }
    },
    [setActiveCommentId]
  );

  // Handle comment reply — detect @martha mentions and route to AI
  const handleCommentReply = useCallback(
    (commentId: string, content: string) => {
      replyToComment(commentId, content);

      // Detect @martha mention and also ask Martha AI
      if (projectId && /@martha/i.test(content)) {
        const question = content.replace(/@martha/gi, '').trim();
        if (question) {
          fetch(`/api/orchestration/conversations/${projectId}/ask`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              question,
              documentId: resolvedDocId,
              documentTitle: activeDocument?.title,
            }),
          })
            .then((res) => res.ok ? res.json() as Promise<{ data: { answer: string } }> : null)
            .then((body) => {
              if (body?.data?.answer) {
                // Post Martha's response as a follow-up reply
                replyToComment(commentId, body.data.answer);
              }
            })
            .catch(() => { /* silent */ });
        }
      }
    },
    [replyToComment, projectId, resolvedDocId, activeDocument?.title]
  );

  // Handle comment resolve
  const handleCommentResolve = useCallback(
    (commentId: string) => {
      resolveComment(commentId);
    },
    [resolveComment]
  );

  // Handle trash document
  const handleTrashDocument = useCallback(async () => {
    if (!resolvedDocId || !projectId) return;
    try {
      await documentsApi.delete(resolvedDocId, projectId);
      queryClient.invalidateQueries({ queryKey: ['project-documents', projectId] });
      toast.success('Document moved to trash');
      setShowTrashDialog(false);
      // Navigate to next doc or doc list
      const remaining = orderedDocs.filter(d => d.id !== resolvedDocId);
      if (remaining.length > 0) {
        navigate(buildProjectUrl(`${routeSegment}/${remaining[0].id}`, paneCode));
      } else {
        navigate(buildProjectUrl(routeSegment, paneCode));
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to move document to trash';
      toast.error(message);
    }
  }, [resolvedDocId, projectId, queryClient, toast, orderedDocs, navigate, buildProjectUrl, routeSegment, paneCode]);

  // Handle add link
  const handleAddLink = useCallback(() => {
    setShowAddLinkDialog(true);
  }, []);

  // Handle create link (from dialog)
  const handleCreateLink = useCallback(
    async (targetDocId: string, relationType: DocumentRelationType) => {
      await createLink(targetDocId, relationType);
      setShowAddLinkDialog(false);
    },
    [createLink]
  );

  // Handle delete link
  const handleDeleteLink = useCallback(
    (relationId: string) => {
      deleteLink(relationId);
    },
    [deleteLink]
  );

  // Handle linked doc click (from Related tab).
  // Accepts an optional blockId so deep links can be passed through the same
  // entry point the sidebar uses.
  const handleLinkDocClick = useCallback(
    (docId: string, blockId?: string) => {
      const base = buildProjectUrl(`${routeSegment}/${docId}`, paneCode);
      const hash = blockId ? `#block:${blockId}` : '';
      navigate(`${base}${hash}`);
    },
    [navigate, buildProjectUrl, routeSegment, paneCode]
  );

  // ── Intercept same-origin anchor clicks that target a #block:<id> hash ──
  // Martha-generated deep links show up as plain anchor tags in chat/AI
  // rendered markdown. Without interception the browser does a full page
  // load; intercept here and route through navigate() so the hash-scroll
  // effect fires and we stay in the SPA.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!anchor) return;
      const href = anchor.getAttribute('href') || '';
      // Only intercept same-origin deep links to a document with a block hash.
      if (!/^\/w\/[^/]+\/[^/]+\/[^/]+\/documents\/[^/#?]+#block:[A-Za-z0-9_-]+$/.test(href)) return;
      e.preventDefault();
      navigate(href);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [navigate]);

  // ── Listen for documentLink block/mark click events ──
  // DocumentLinkBlock dispatches a bubbling CustomEvent<{documentId, documentName, targetBlockId?}>
  // when the user clicks the inline chip. Intercept it here and navigate
  // through React Router so we stay within the SPA and the hash triggers the
  // deep-link scroll effect below.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent).detail as
        | { documentId?: string; targetBlockId?: string }
        | undefined;
      if (!detail?.documentId) return;
      handleLinkDocClick(detail.documentId, detail.targetBlockId || undefined);
    };
    window.addEventListener('documentLinkClick', handler);
    return () => window.removeEventListener('documentLinkClick', handler);
  }, [handleLinkDocClick]);

  // ── Deep-link scroll: #block:<id> hash scrolls to a specific paragraph ──
  //
  // BlockNote attaches data-id to every rendered block. On mount and whenever
  // the hash changes, look for a matching block and scroll it into view with
  // a brief highlight flash so the user sees where they landed. Uses a
  // MutationObserver so we pick the block up whenever chunked hydration
  // finally inserts it (can take 10+s for large docs).
  useEffect(() => {
    if (!activeDocId) return;
    const match = location.hash.match(/^#block:([A-Za-z0-9_-]+)$/);
    if (!match) return;
    const blockId = match[1];
    const selector = `[data-id="${CSS.escape(blockId)}"]`;

    let done = false;
    const flash = (el: Element) => {
      if (done) return;
      done = true;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const target = el as HTMLElement;
      // Use the Web Animations API — adding a className is racy because
      // BlockNote rerenders the block and overwrites class strings.
      target.animate(
        [
          { backgroundColor: 'rgba(249, 115, 22, 0.22)', borderRadius: '4px' },
          { backgroundColor: 'transparent', borderRadius: '4px' },
        ],
        { duration: 2000, easing: 'ease-out' },
      );
      // data attribute gives tests + observability a stable signal without
      // relying on a transient animation state.
      target.setAttribute('data-deep-link-flashed', 'true');
    };

    // Check immediately in case the block is already there.
    const existing = document.querySelector(selector);
    if (existing) {
      flash(existing);
      return;
    }

    // Otherwise watch for it to appear.
    const observer = new MutationObserver(() => {
      const el = document.querySelector(selector);
      if (el) {
        flash(el);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Hard cap so we don't observe forever on a doc that never contains the
    // referenced block.
    const timeout = window.setTimeout(() => observer.disconnect(), 20_000);

    return () => {
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, [activeDocId, location.hash]);

  // Persistent highlights for commented text using CSS Custom Highlight API
  useEffect(() => {
    if (!activeDocId || comments.length === 0) {
      // Clear highlights when no comments
      if ('highlights' in CSS) {
        (CSS as { highlights: Map<string, unknown> }).highlights.delete('comment-highlight');
      }
      return;
    }

    // Wait for the editor to render
    const timer = setTimeout(() => {
      const editorEl = document.querySelector('.blocknote-editor');
      if (!editorEl) return;

      const openComments = comments.filter(c => c.status === 'open' && c.selectedText);

      if ('highlights' in CSS) {
        // Use CSS Custom Highlight API
        const ranges: Range[] = [];

        for (const comment of openComments) {
          const walker = document.createTreeWalker(editorEl, NodeFilter.SHOW_TEXT);
          let node: Text | null;
          while ((node = walker.nextNode() as Text | null)) {
            const idx = node.textContent?.indexOf(comment.selectedText) ?? -1;
            if (idx === -1) continue;
            const range = new Range();
            range.setStart(node, idx);
            range.setEnd(node, idx + comment.selectedText.length);
            ranges.push(range);
            break;
          }
        }

        if (ranges.length > 0) {
          const highlight = new Highlight(...ranges);
          (CSS as { highlights: Map<string, Highlight> }).highlights.set('comment-highlight', highlight);
        } else {
          (CSS as { highlights: Map<string, unknown> }).highlights.delete('comment-highlight');
        }
      } else {
        // Fallback: DOM-based highlighting with <mark> elements
        // Clean up previous highlights
        editorEl.querySelectorAll('.comment-persistent-highlight').forEach(el => {
          const parent = el.parentNode;
          if (parent) {
            parent.replaceChild(document.createTextNode(el.textContent || ''), el);
            parent.normalize();
          }
        });

        for (const comment of openComments) {
          const walker = document.createTreeWalker(editorEl, NodeFilter.SHOW_TEXT);
          let node: Text | null;
          while ((node = walker.nextNode() as Text | null)) {
            const idx = node.textContent?.indexOf(comment.selectedText) ?? -1;
            if (idx === -1) continue;
            const range = document.createRange();
            range.setStart(node, idx);
            range.setEnd(node, idx + comment.selectedText.length);
            const mark = document.createElement('mark');
            mark.className = 'comment-persistent-highlight';
            range.surroundContents(mark);
            break;
          }
        }
      }
    }, 800);

    return () => {
      clearTimeout(timer);
      if ('highlights' in CSS) {
        (CSS as { highlights: Map<string, unknown> }).highlights.delete('comment-highlight');
      }
    };
  }, [activeDocId, comments]);

  // ── Register TOC/Comments/Related tabs in the global right sidebar ──
  const openCommentsCount = comments.filter((c) => c.status === 'open').length;
  const hasBlockers = documentLinks.some(l => l.relation_type === 'blocked_by');

  useEffect(() => {
    if (!activeDocument) {
      unregisterTabs();
      return;
    }

    const tabs: PanelTabRegistration[] = [
      {
        id: 'toc',
        icon: <List size={16} />,
        title: 'Contents',
        render: () => <DocOutlineContent document={activeDocument as EditorDocument} />,
      },
      {
        id: 'comments',
        icon: <MessageCircle size={16} />,
        title: 'Comments',
        badge: openCommentsCount > 0,
        badgeColor: 'coral' as const,
        render: () => (
          <CommentsSidebar
            comments={comments}
            repliesMap={repliesMap}
            activeCommentId={activeCommentId}
            onCommentClick={handleCommentClick}
            onReply={handleCommentReply}
            onResolve={handleCommentResolve}
            onAskMartha={projectId ? (ctx: { selectedText: string; commentContent: string }) => {
              fetch(`/api/orchestration/conversations/${projectId}/ask`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  question: `Regarding this text: "${ctx.selectedText}"\n\nComment: ${ctx.commentContent}\n\nPlease provide context and help.`,
                  documentId: resolvedDocId,
                  documentTitle: activeDocument?.title,
                }),
              })
                .then((res) => res.ok ? res.json() as Promise<{ data: { answer: string } }> : null)
                .then((body) => {
                  if (body?.data?.answer) {
                    toast.success(body.data.answer.slice(0, 300));
                  }
                })
                .catch(() => { /* silent */ });
            } : undefined}
            isSubmitting={isReplying || isResolving}
          />
        ),
      },
      {
        id: 'related',
        icon: <Link2 size={16} />,
        title: 'Related',
        badge: hasBlockers,
        badgeColor: 'amber' as const,
        render: () => (
          <RelatedSidebar
            links={documentLinks ?? []}
            onDeleteLink={handleDeleteLink}
            onAddLink={handleAddLink}
            onLinkDocClick={handleLinkDocClick}
            isDeleting={isLinkDeleting}
          />
        ),
      },
    ];

    registerTabs(tabs);
    return () => unregisterTabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeDocument, comments, documentLinks, repliesMap, activeCommentId,
    openCommentsCount, hasBlockers, projectId, resolvedDocId,
  ]);

  // ── Create-menu state + header actions for sidebar ──
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const createOptions: { type: DocumentCategory; label: string; icon: React.ReactNode }[] = [
    { type: 'requirements', label: 'Requirement', icon: <ClipboardCheck size={16} /> },
    { type: 'specifications', label: 'Specification', icon: <FileCode size={16} /> },
    { type: 'tests', label: 'Test Plan', icon: <FlaskConical size={16} /> },
    { type: 'general', label: 'General Doc', icon: <FileText size={16} /> },
  ];
  const sidebarHeaderActions = (
    <div className="relative">
      <button
        onClick={() => setShowCreateMenu(!showCreateMenu)}
        className="p-1.5 rounded-lg bg-coral-500 text-white hover:bg-coral-600 transition-colors"
      >
        <Plus size={14} />
      </button>
      {showCreateMenu && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setShowCreateMenu(false)} />
          <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-20">
            {createOptions.map((option) => (
              <button
                key={option.type}
                onClick={() => { handleCreateDocument(option.type); setShowCreateMenu(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-neutral-700 hover:bg-neutral-50 transition-colors"
              >
                <span className="text-neutral-500">{option.icon}</span>
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <PageLayout variant="full-width" contentBackground="#ffffff">
      <div className="h-full flex">
        {/* Left Sidebar - Pivot-grouped document tree */}
        <PivotSidebar<EditorDocument>
          title={sidebarTitle}
          items={documents}
          dimensions={DOC_DIMENSIONS}
          defaultDimension={defaultDimension}
          activeItemId={resolvedDocId}
          isLoading={isLoadingDocs}
          headerActions={sidebarHeaderActions}
          getItemId={(doc) => doc.id}
          getItemTitle={(doc) => doc.title}
          getItemUpdatedAt={(doc) => doc.updated_at}
          getItemSearchText={(doc) =>
            [doc.title, ...(doc.tags || [])].join(' ')
          }
          onItemSelect={handleDocumentSelect}
          storageKey={`docs-pivot-${projectId || 'none'}`}
          getParentId={(doc) => doc.parent_doc_id || null}
        />

        {/* Center - Document Content or List */}
        {!resolvedDocId ? (
          <DocListCenter
            documents={documents}
            isLoading={isLoadingDocs}
            onDocumentSelect={handleDocumentSelect}
            onCreateDocument={handleCreateDocument}
          />
        ) : (
          <ReaderCenterShell
            headerLeft={
              <div className="flex items-center gap-1">
                <button
                  onClick={handleNavigatePrev}
                  disabled={!hasPrev}
                  className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100
                           disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={handleNavigateNext}
                  disabled={!hasNext}
                  className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100
                           disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            }
            headerContent={
              <div className="flex items-center gap-3">
                {updateMutation.isPending ? (
                  <span className="flex items-center gap-1.5 text-sm text-neutral-500">
                    <Loader2 size={14} className="animate-spin" />
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-sm text-neutral-400">
                    <Save size={14} />
                    Auto-saved
                  </span>
                )}
                <PresenceAvatars
                  users={connectedUsers}
                  isConnected={isConnected}
                  isConnecting={isConnecting}
                  currentUserId={user?.id}
                />
                {/* Document actions menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowDocActions(!showDocActions)}
                    className="p-1.5 rounded-lg text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 transition-colors"
                  >
                    <MoreHorizontal size={18} />
                  </button>
                  {showDocActions && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowDocActions(false)} />
                      <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-lg shadow-lg border border-neutral-200 py-1 z-20">
                        <button
                          onClick={() => { setShowTrashDialog(true); setShowDocActions(false); }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                        >
                          <Trash2 size={16} />
                          Move to Trash
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            }
            footer={
              <div className="flex items-center justify-between px-6 py-3 border-t border-neutral-100 bg-neutral-50/80">
                <button
                  onClick={handleNavigatePrev}
                  disabled={!hasPrev}
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900
                           disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>
                <button
                  onClick={handleNavigateNext}
                  disabled={!hasNext}
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900
                           disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            }
            isLoading={isLoadingDoc && !activeDocument}
          >
            {docError && !activeDocument && (
              <div className="flex flex-col items-center justify-center py-20 text-neutral-500">
                <p className="text-sm font-medium text-neutral-600 mb-1">Failed to load document</p>
                <p className="text-xs text-neutral-400 mb-3">
                  {(docError as Error)?.message || 'Unknown error'}
                </p>
                <button
                  onClick={() => queryClient.invalidateQueries({ queryKey: ['document', projectId, resolvedDocId] })}
                  className="text-xs text-coral-600 hover:text-coral-700 underline"
                >
                  Retry
                </button>
              </div>
            )}
            {activeDocument && (
              <DocContent
                document={activeDocument as EditorDocument}
                projectId={projectId}
                isSaving={updateMutation.isPending}
                onSave={handleSave}
                onTitleChange={handleTitleChange}
                onSelectionChange={handleSelectionChange}
                onCommentClick={handleCommentButtonClick}
                collaborationConfig={collaborationConfig}
                isCollaborationConnected={isConnected}
                isCollaborationSynced={isCollaborationSynced}
                collaborationUsers={connectedUsers?.map(u => ({
                  id: u.id || u.name,
                  name: u.name,
                  color: u.color,
                }))}
              />
            )}

            {/* Comment Popover - appears when comment toolbar button is clicked */}
            {showCommentPopover && commentSelection && (
              <CommentPopover
                selectedText={commentSelection.text}
                selectionStart={commentSelection.start}
                selectionEnd={commentSelection.end}
                position={{ top: commentSelection.position.y, left: commentSelection.position.x }}
                onSubmit={(content) => {
                  handleCreateComment(content);
                  setShowCommentPopover(false);
                }}
                onCancel={() => {
                  setShowCommentPopover(false);
                  setCommentSelection(null);
                }}
                onAskMartha={(text) => {
                  chatSelection.setSelection(text, resolvedDocId);
                  window.dispatchEvent(new CustomEvent('martha:expand-chat'));
                  setShowCommentPopover(false);
                }}
              />
            )}
          </ReaderCenterShell>
        )}

        {/* Right sidebar tabs (TOC, Comments, Related) registered via useRightPanelRegistry */}
      </div>

      {/* Add Link Dialog */}
      {showAddLinkDialog && (
        <AddLinkDialog
          documents={documents}
          currentDocId={resolvedDocId || ''}
          onSubmit={handleCreateLink}
          onClose={() => setShowAddLinkDialog(false)}
          isCreating={isLinkCreating}
        />
      )}

      {/* Template Selector Modal (for requirements) */}
      {showTemplateSelector && (
        <TemplateSelector
          isOpen={showTemplateSelector}
          onClose={() => setShowTemplateSelector(false)}
          onSelect={handleTemplateSelect}
        />
      )}

      {/* Confirm Trash Dialog */}
      <ConfirmTrashDialog
        open={showTrashDialog}
        onClose={() => setShowTrashDialog(false)}
        onConfirm={handleTrashDocument}
        documentTitle={(activeDocument as EditorDocument)?.title || 'this document'}
      />
    </PageLayout>
  );
}

// Generate template content based on selected template
function generateTemplateContent(template: RequirementsTemplate): string {
  const sections = template.sections || [];

  if (sections.length === 0) {
    // Blank + AI Guide template
    return `# ${template.name}

*Start writing your requirements here. Martha will guide you through each section.*

`;
  }

  let content = `# ${template.name}

`;

  for (const section of sections) {
    content += `## ${section.title}

*${getPlaceholderText(section.type)}*

`;
  }

  return content;
}

// Get placeholder text for each section type
function getPlaceholderText(sectionType: string): string {
  const placeholders: Record<string, string> = {
    overview: 'Provide a brief overview of this requirement document. What is the purpose and scope?',
    problem_statement: 'Describe the problem you are solving. Who experiences it? What is the impact?',
    user_needs: 'List the user needs this requirement addresses. What do users want to achieve?',
    user_stories: 'Write user stories in the format: As a [type of user], I want [goal] so that [benefit].',
    functional_requirements: 'List the functional requirements. What should the system do? Use "The system shall..." format.',
    non_functional_requirements: 'List non-functional requirements like performance, security, scalability, etc.',
    acceptance_criteria: 'Define how you will know when requirements are met. Use Given/When/Then format.',
    constraints: 'List any constraints or limitations that affect the implementation.',
    assumptions: 'Document assumptions made while writing these requirements.',
    dependencies: 'List dependencies on other systems, teams, or requirements.',
  };

  return placeholders[sectionType] || 'Add content for this section.';
}
